import { twMerge } from '@shared/themes';
import { imagePathResolver, NumberFormats, ROUTES, useClientConfig, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { ClassicGameCard } from '../../ClassicGameCard/ClassicGameCard';
import { ScratchcardBackground } from '../ScratchcardBackground/ScratchcardBackground';
import type { ScratchcardDetails } from '../types';

type ScratchcardsCardGridProps = ComponentProps<'div'> & {
  scratchcards: ScratchcardDetails[];
  namespace: string;
};

const BACKGROUND_COLORS = [
  'bg-eurodreams',
  'bg-megadavirada',
  'bg-accent-orange',
  'bg-accent-green',
  'bg-elgordodeverano',
  'bg-malamaaldaily',
  'bg-cash4life',
  'bg-germankeno',
  'bg-accent-pink',
  'bg-quina',
  'bg-6aus49go-special',
  'bg-elnino',
  'bg-accent-purple',
  'bg-accent-neonblue',
  'bg-accent-blue',
];

function ScratchcardsCardGrid({ scratchcards, namespace, className, ...props }: ScratchcardsCardGridProps) {
  const t = useTranslations(namespace);
  const { baseImgUrl } = useClientConfig();
  const currencyFormatter = useCurrencyFormatter();

  return (
    <div className={twMerge('grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4', className)} {...props}>
      {scratchcards.map((card, index) => {
        const minPrice = card.bundles && card.bundles.length > 0 ? Math.min(...card.bundles.map(bundle => bundle.price)) : 0;
        const backgroundColor = BACKGROUND_COLORS[index % BACKGROUND_COLORS.length] ?? 'bg-eurodreams';
        const priceFormatted = currencyFormatter(minPrice).format(NumberFormats.WITH_DECIMALS).get();
        return (
          <ClassicGameCard
            key={card.title}
            variant="list"
            id={card.title}
            gameName={card.localisedName}
            subtitle={t('per_card', { price: priceFormatted })}
            backgroundImage={imagePathResolver(card.smallImageUrl ?? '', baseImgUrl, true)}
            href={ROUTES.SCRATCHCARDS.GAME_INFO(card.mappingPath)}
            playButtonText={t('play')}
            gameProvider=""
            position={index + 1}
            showFavouriteButton={false}
            showInfoButton={false}
            backgroundElement={
              <div className={`absolute inset-0 ${backgroundColor}`}>
                <ScratchcardBackground position={index + 1} data-testid="scratchcard-background-svg" />
              </div>
            }
            jackpotLabelText={card.topTip}
          />
        );
      })}
    </div>
  );
}

export { ScratchcardsCardGrid, type ScratchcardsCardGridProps };
